import PropTypes from 'prop-types'
import { Modal, Text, TextButton, PrimaryButton, IconButton, Icon } from '@artprov/design-system'
import { MODAL_MESSAGES } from '../constants/formConstants'

/**
 * Modal de confirmación para dar de baja la relación con una cuenta
 * (contrato activo o solicitud pendiente) desde la sección "Mis cuentas".
 *
 * @param {boolean} open - Indica si el modal está abierto
 * @param {Function} setOpen - Callback para abrir/cerrar el modal
 * @param {string} razonSocial - Razón social de la cuenta seleccionada (dinámica)
 * @param {boolean} isSubmitting - Indica si la baja está en curso (deshabilita botones)
 * @param {Function} onCancel - Callback al cancelar
 * @param {Function} onConfirm - Callback al confirmar la baja
 */
export default function ModalEliminarCuenta({
  open,
  setOpen,
  razonSocial = '',
  isSubmitting = false,
  onCancel,
  onConfirm,
}) {
  const handleClose = () => {
    if (isSubmitting) return
    setOpen(false)
  }

  const handleCancel = () => {
    handleClose()
    onCancel?.()
  }

  const handleConfirm = () => {
    if (isSubmitting) return
    onConfirm?.()
  }

  return (
    <Modal.Container open={open} setOpen={(value) => (isSubmitting ? undefined : setOpen(value))}>
      <Modal.Content width="min(95vw, 435px)" height="auto">
        <div className="py-5 px-5">
          {/* Icono de advertencia y botón cerrar */}
          <div className="flex justify-between items-start mb-5">
            <div className="w-full flex flex-col items-center">
              <div className="w-16 h-16 rounded-full flex items-center justify-center">
                <Icon icon="error" size="extra-large" variant="notification warning" />
              </div>
            </div>
            <IconButton
              icon="close"
              size="large"
              className="w-5 h-5 absolute top-4 right-4"
              onClick={handleClose}
              aria-label="Cerrar modal"
            />
          </div>

          {/* Título dinámico con la Razón Social del registro seleccionado */}
          <div className="text-center mb-4">
            <Text variant="h5" className="text-primary-10 font-semibold text-center">
              {MODAL_MESSAGES.DELETE_TITLE_PREFIX} {razonSocial}
            </Text>
          </div>

          {/* Mensaje */}
          <div className="text-center mb-6">
            <Text variant="body1" className="text-tertiary-10 text-center">
              {MODAL_MESSAGES.DELETE_MESSAGE}
            </Text>
          </div>

          {/* Botones */}
          <div className="flex flex-col-reverse sm:flex-row justify-center gap-4">
            <TextButton
              onClick={handleCancel}
              size="small"
              descriptionTextVariant="button2"
              disabled={isSubmitting}
              className="!h-[40px] !min-h-[40px] !px-4 whitespace-nowrap inline-flex items-center justify-center"
            >
              Cancelar
            </TextButton>

            <PrimaryButton
              onClick={handleConfirm}
              size="small"
              descriptionTextVariant="button2"
              disabled={isSubmitting}
              className="!h-[40px] !min-h-[40px] !px-6 whitespace-nowrap inline-flex items-center justify-center"
            >
              {isSubmitting ? 'Procesando...' : MODAL_MESSAGES.DELETE_CONFIRM_BUTTON}
            </PrimaryButton>
          </div>
        </div>
      </Modal.Content>
    </Modal.Container>
  )
}

ModalEliminarCuenta.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  razonSocial: PropTypes.string,
  isSubmitting: PropTypes.bool,
  onCancel: PropTypes.func,
  onConfirm: PropTypes.func,
}
